export type MetadataFilterOption = {
  key: string;
  value: string;
  count: number;
};

type MetadataSource = {
  additionalMetadata?: object;
  children?: MetadataSource[];
};

const IDENTIFIER_KEY =
  /(^|[_-])(id|uuid|traceparent|tracestate|token|hash)$|(?:Id|ID|Uuid|UUID)$/;

function getKeyPriority(key: string) {
  if (key.startsWith('highstakes__')) {
    return 2;
  }
  return IDENTIFIER_KEY.test(key) ? 1 : 0;
}

function collectMetadataOptions(
  runs: MetadataSource[],
  options: Map<string, MetadataFilterOption>,
) {
  for (const run of runs) {
    for (const [key, value] of Object.entries(run.additionalMetadata ?? {})) {
      if (!key || key.includes(':') || typeof value !== 'string') {
        continue;
      }

      const id = JSON.stringify([key, value]);
      const current = options.get(id);
      options.set(id, { key, value, count: current ? current.count + 1 : 1 });
    }

    if (run.children) {
      collectMetadataOptions(run.children, options);
    }
  }
}

export function buildMetadataFilterOptions(runs: MetadataSource[]) {
  const options = new Map<string, MetadataFilterOption>();
  collectMetadataOptions(runs, options);
  const keyCounts = new Map<string, number>();
  for (const option of options.values()) {
    keyCounts.set(option.key, (keyCounts.get(option.key) ?? 0) + option.count);
  }

  return [...options.values()].sort((left, right) => {
    const priority = getKeyPriority(left.key) - getKeyPriority(right.key);
    if (priority || left.key === right.key) {
      return (
        priority ||
        right.count - left.count ||
        left.value.localeCompare(right.value)
      );
    }

    return (
      keyCounts.get(right.key)! - keyCounts.get(left.key)! ||
      left.key.localeCompare(right.key)
    );
  });
}
